import { useMemo, useState } from 'react';
import { REGIONS } from '../data/regions';
import { ROUTES } from '../data/routes';
import { LANDMARKS } from '../data/landmarks';
import type { MapEntity } from '../data/types';
import styles from './SearchBar.module.css';

interface SearchBarProps {
  onSelect: (entity: MapEntity) => void;
}

interface SearchHit {
  entity: MapEntity;
  label: string;
  brief: string;
}

function allHits(): SearchHit[] {
  const out: SearchHit[] = [];
  for (const r of REGIONS)
    out.push({ entity: { type: 'region', id: r.id }, label: r.name, brief: r.brief });
  for (const r of ROUTES)
    out.push({ entity: { type: 'route', id: r.id }, label: r.name, brief: r.description.slice(0, 60) + '…' });
  for (const lm of LANDMARKS)
    out.push({ entity: { type: 'landmark', id: lm.id }, label: lm.name, brief: lm.brief });
  return out;
}

export function SearchBar({ onSelect }: SearchBarProps) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const hits = useMemo(() => allHits(), []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    return hits
      .filter((h) => h.label.toLowerCase().includes(query) || h.brief.toLowerCase().includes(query))
      .slice(0, 8);
  }, [q, hits]);

  return (
    <div className={styles.wrap}>
      <input
        type="search"
        className={styles.input}
        placeholder="search the map…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      {open && filtered.length > 0 && (
        <ul className={styles.results}>
          {filtered.map((hit, i) => (
            <li key={i}>
              <button
                type="button"
                className={styles.hit}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(hit.entity);
                  setQ('');
                  setOpen(false);
                }}
              >
                <span className={styles.hitLabel}>{hit.label}</span>
                <span className={styles.hitBrief}>{hit.brief}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
